import { defineStore } from "pinia";
import type UserProfileBasedSecurity from "@/interfaces/Security/UserProfileBasedSecurity";
import type WebLogin from "@/interfaces/WebLogin/WebLogin";
import type WebLoginAccount from "@/interfaces/WebLogin/WebLoginAccount";
import type WebLoginAuthenticationTokens from "@/interfaces/WebLogin/WebLoginAuthenticationTokens";
import EmployeeSecurity from "@/enumerations/Security/EmployeeSecurity";
import YesNoSecurity from "@/enumerations/Security/YesNoSecurity";
import WebLoginAccountFactory from "@/factories/WebLogin/WebLoginAccountFactory";
import {
  getNewerKeptSessionTokens,
  getWebLoginFromJsonWebToken,
  isKeptSessionOf,
} from "@/helpers/WebLogin/WebLoginHelpers";
import { getUserProfileBasedSecurity } from "@/repositories/Security/UserProfileBasedSecurityRepository";
import { recordAccountSelection } from "@/repositories/Security/LoginActivityRepository";
import { getIsOilEnergyCustomer } from "@/repositories/OilEnergy/OilEnergyRepository";
import { getAccountKey } from "@/helpers/WebLogin/SelectedAccountKey";
import { useParametersStore } from "@/stores/parameters";

const SESSION_KEYS = ["tokens", "selectedAccount", "userProfileBasedSecurity"];

function removeSessionKeys(storage: Storage) {
  SESSION_KEYS.forEach((key) => storage.removeItem(key));
}
//One read is shared by the account selection that starts it and the route guard that waits on it, so opening an
//Oil & Energy page straight after switching accounts asks Merchant once rather than twice.
let oilEnergyEligibilityRequest:
  | { accountKey: string; promise: Promise<void> }
  | undefined;

function clearStorageKeys() {
  removeSessionKeys(localStorage);
  removeSessionKeys(sessionStorage);
}

function getStorage(keepLoggedIn: boolean) {
  return keepLoggedIn ? localStorage : sessionStorage;
}

function parseStoredJson<T>(serializedValue: string, key: string): T {
  try {
    return JSON.parse(serializedValue) as T;
  } catch (error) {
    throw new Error(
      `Stored ${key} is invalid JSON: ${
        error instanceof Error ? error.message : "Unknown parsing error."
      }`
    );
  }
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    selectedAccount: undefined as WebLoginAccount | undefined,
    webLogin: {} as WebLogin,
    tokens: undefined as WebLoginAuthenticationTokens | undefined,
    userProfileBasedSecurity: undefined as UserProfileBasedSecurity | undefined,
    sessionRestored: false,
    oilEnergyAccountKey: undefined as string | undefined,
  }),
  getters: {
    isAuthenticated(state) {
      return state.tokens !== undefined;
    },
    /**
     * False for a restored session as well as a freshly selected account: the flag is persisted with the
     * account but the answer is Merchant's to give, so it is asked again rather than trusted from storage.
     */
    isOilEnergyResolved(state) {
      return (
        !state.selectedAccount?.id ||
        state.oilEnergyAccountKey === getAccountKey(state.selectedAccount)
      );
    },
    /**
     * Whether the user profile based check stands between this login and the Oil & Energy endpoints, which is
     * the same line SecuritySettingsBase.checkUserProfileBasedSecurity draws: it applies only to an employee
     * whose security is user profile based and who is looking at an account that is not their own.
     */
    isOilEnergyProfileChecked(state) {
      return (
        state.webLogin.isEmployee === true &&
        state.webLogin.employeeSecurity === EmployeeSecurity.userProfile &&
        !state.selectedAccount?.isAccountLinkedToWebLogin
      );
    },
  },
  actions: {
    persistSessionState() {
      // with no session here there is nothing of this tab's to keep, and the one other tabs keep is not this tab's
      if (!this.tokens) {
        removeSessionKeys(sessionStorage);
        return;
      }

      if (this.webLogin.keepLoggedIn) {
        // another tab may have refreshed the shared session since this one did; writing these tokens back would
        // leave a refresh token the API no longer holds
        const newerTokens = getNewerKeptSessionTokens(this.tokens);
        if (newerTokens) {
          this.tokens = { ...newerTokens };
          this.webLogin = getWebLoginFromJsonWebToken(newerTokens.jsonWebToken);
        }
      }

      // a session not kept is this tab's alone, so the one other tabs keep in local storage is left as it is
      removeSessionKeys(sessionStorage);
      const storage = getStorage(this.webLogin.keepLoggedIn);

      storage.setItem("tokens", JSON.stringify(this.tokens));

      if (this.selectedAccount) {
        storage.setItem("selectedAccount", JSON.stringify(this.selectedAccount));
      } else {
        storage.removeItem("selectedAccount");
      }

      if (this.userProfileBasedSecurity) {
        storage.setItem(
          "userProfileBasedSecurity",
          JSON.stringify(this.userProfileBasedSecurity)
        );
      } else {
        storage.removeItem("userProfileBasedSecurity");
      }
    },
    // A tab kept logged in shares its session with every other kept tab, so signing out in one ends it in all. The
    // browser keeps one such session, so another login kept logged in in another tab ends it too, rather than the
    // two writing over each other.
    endSessionSignedOutInAnotherTab(event: StorageEvent): boolean {
      if (!this.tokens || !this.webLogin.keepLoggedIn || event.storageArea !== localStorage) {
        return false;
      }

      const sharedSessionRemoved =
        localStorage.getItem("tokens") === null &&
        (event.key === null ||
          (event.key === "tokens" && isKeptSessionOf(event.oldValue, this.tokens.jsonWebToken)));
      const sharedSessionTakenByAnotherLogin =
        event.key === "tokens" &&
        event.newValue !== null &&
        isKeptSessionOf(event.oldValue, this.tokens.jsonWebToken) &&
        !isKeptSessionOf(event.newValue, this.tokens.jsonWebToken);

      if (!sharedSessionRemoved && !sharedSessionTakenByAnotherLogin) {
        return false;
      }

      this.clearState();
      return true;
    },
    setWebTokens(tokens: WebLoginAuthenticationTokens | undefined) {
      if (!tokens) {
        this.tokens = undefined;
        this.webLogin = {} as WebLogin;
        this.persistSessionState();
        return;
      }

      const webLogin = getWebLoginFromJsonWebToken(tokens.jsonWebToken);

      this.tokens = { ...tokens };
      this.webLogin = webLogin;
      this.persistSessionState();
    },
    async setSelectedAccount(
      selectedAccount: WebLoginAccount | undefined,
      reloadEntityParameters = true
    ) {
      this.selectedAccount = selectedAccount
        ? new WebLoginAccountFactory(selectedAccount).getInstance()
        : undefined;
      this.oilEnergyAccountKey = undefined;
      this.persistSessionState();

      //The audit row is what the Login Activity tab reads, so opening or switching an account has to write
      //one. It is deliberately not awaited and its failure is swallowed: an audit row that cannot be written
      //is not a reason to stop a grower reaching their account.
      if (this.selectedAccount?.id) {
        recordAccountSelection().catch(() => undefined);
        //Started here rather than awaited so switching accounts is not held up by the read; the route guard
        //waits on the same request when the grower is heading somewhere that needs the answer.
        this.loadOilEnergyEligibility();
      }

      const parametersStore = useParametersStore();

      if (reloadEntityParameters && this.selectedAccount?.entityId) {
        await parametersStore.loadEntityParameterSettings(true);
      } else if (!this.selectedAccount?.entityId) {
        parametersStore.clearEntityParameterSettings();
      }
    },
    /**
     * Amends the account already open rather than opening one. Removing the grower's only other account leaves
     * them on the same account with Switch Account gone, which is not something they did to the account: it
     * writes no Account Opened row for the Login Activity tab to show, and does not send Merchant back over
     * eligibility or entity parameters that cannot have changed.
     */
    updateSelectedAccountDetails(selectedAccount: WebLoginAccount) {
      this.selectedAccount = new WebLoginAccountFactory(
        selectedAccount
      ).getInstance();
      this.persistSessionState();
    },
    setUserProfileBasedSecurity(
      userProfileBasedSecurity: UserProfileBasedSecurity | undefined
    ) {
      this.userProfileBasedSecurity = userProfileBasedSecurity
        ? { ...userProfileBasedSecurity }
        : undefined;
      this.persistSessionState();
    },
    /**
     * Records Merchant's answer against the account it was asked about, so a slow reply cannot be stamped onto
     * an account the grower has since switched away from. The key includes the entity because the same customer
     * identifier exists in more than one entity database.
     */
    setOilEnergyEligibility(accountKey: string, isOilEnergyCustomer: boolean) {
      if (getAccountKey(this.selectedAccount) !== accountKey) {
        return;
      }

      this.selectedAccount = {
        ...this.selectedAccount,
        isOilEnergyCustomer: isOilEnergyCustomer,
      } as WebLoginAccount;
      this.oilEnergyAccountKey = accountKey;
      this.persistSessionState();
    },
    /**
     * The Oil & Energy section is hidden unless the customer is flagged for it in Merchant, and the route guard
     * waits on this answer, so it has to settle for every login rather than only the ones Merchant will answer.
     *
     * One of them never reaches Merchant. A profile that is not granted Oil & Energy is refused by the endpoint
     * whatever Merchant would have answered, so it is settled here rather than spending a request to be told so.
     * The refusal is a 403 now, which the client no longer mistakes for an expired session, but the request it
     * saves was never worth making.
     *
     * A read that fails is tried once more and then settled rather than left open, because leaving it open asks
     * again on every navigation. One retry covers the blip that would otherwise hide the section for the rest
     * of the session; switching account or reloading asks again. The read suppresses the generic alert, so a
     * failure hides the section quietly rather than putting a banner over a page that works.
     */
    loadOilEnergyEligibility(): Promise<void> {
      const accountKey = getAccountKey(this.selectedAccount);

      if (this.isOilEnergyResolved) {
        return Promise.resolve();
      }

      if (this.isOilEnergyProfileChecked) {
        //Selecting an account starts this read before the profile rights are in hand. Nothing is settled on a
        //guess: the route guard loads the rights before it waits on this, so the next call has them.
        if (!this.userProfileBasedSecurity) {
          return Promise.resolve();
        }

        if (this.userProfileBasedSecurity.oilEnergy < YesNoSecurity.yes) {
          this.setOilEnergyEligibility(accountKey, false);
          return Promise.resolve();
        }
      }

      if (oilEnergyEligibilityRequest?.accountKey === accountKey) {
        return oilEnergyEligibilityRequest.promise;
      }

      const promise = getIsOilEnergyCustomer()
        .catch(() => getIsOilEnergyCustomer())
        .then((isOilEnergyCustomer) => {
          this.setOilEnergyEligibility(accountKey, isOilEnergyCustomer);
        })
        .catch(() => {
          this.setOilEnergyEligibility(accountKey, false);
        })
        .finally(() => {
          if (oilEnergyEligibilityRequest?.accountKey === accountKey) {
            oilEnergyEligibilityRequest = undefined;
          }
        });

      oilEnergyEligibilityRequest = { accountKey: accountKey, promise: promise };

      return promise;
    },
    async loadUserProfileBasedSecurity() {
      const userProfileBasedSecurity = await getUserProfileBasedSecurity();
      this.setUserProfileBasedSecurity(userProfileBasedSecurity);
      return userProfileBasedSecurity;
    },
    restoreSession() {
      if (this.sessionRestored) {
        return;
      }

      const tokensJson =
        sessionStorage.getItem("tokens") ?? localStorage.getItem("tokens");

      if (!tokensJson) {
        this.sessionRestored = true;
        return;
      }

      try {
        const restoredTokens = parseStoredJson<WebLoginAuthenticationTokens>(
          tokensJson,
          "tokens"
        );
        const restoredWebLogin = getWebLoginFromJsonWebToken(
          restoredTokens.jsonWebToken
        );
        const storage = getStorage(restoredWebLogin.keepLoggedIn);
        const selectedAccountJson = storage.getItem("selectedAccount");
        const userProfileBasedSecurityJson = storage.getItem(
          "userProfileBasedSecurity"
        );

        this.tokens = restoredTokens;
        this.webLogin = restoredWebLogin;

        if (selectedAccountJson) {
          this.selectedAccount = new WebLoginAccountFactory(
            parseStoredJson<WebLoginAccount>(selectedAccountJson, "selectedAccount")
          ).getInstance();
          //Eligibility is Merchant's answer, not the browser's. A customer taken off Oil & Energy since the
          //session was stored must not keep the section because a stale copy says so, and a read that fails
          //must leave it hidden, so the restored flag is dropped and asked again.
          this.selectedAccount.isOilEnergyCustomer = false;
          this.oilEnergyAccountKey = undefined;
        }

        if (userProfileBasedSecurityJson) {
          this.userProfileBasedSecurity =
            parseStoredJson<UserProfileBasedSecurity>(
              userProfileBasedSecurityJson,
              "userProfileBasedSecurity"
            );
        }

        this.sessionRestored = true;
      } catch (error) {
        console.error(
          "Failed to restore customer portal session from storage. Clearing stored session state.",
          error
        );
        // a damaged session of this tab's own leaves the one other tabs keep logged in
        removeSessionKeys(sessionStorage.getItem("tokens") === tokensJson ? sessionStorage : localStorage);
        this.clearState();
      }
    },
    clearState() {
      // only a tab holding the kept session ends it, and only its own login's; ending a session not kept, or one
      // already ended, leaves the session other tabs keep logged in
      if (
        this.tokens &&
        this.webLogin.keepLoggedIn === true &&
        isKeptSessionOf(localStorage.getItem("tokens"), this.tokens.jsonWebToken)
      ) {
        clearStorageKeys();
      } else {
        removeSessionKeys(sessionStorage);
      }
      this.selectedAccount = undefined;
      this.webLogin = {} as WebLogin;
      this.tokens = undefined;
      this.userProfileBasedSecurity = undefined;
      this.sessionRestored = true;
      this.oilEnergyAccountKey = undefined;
      oilEnergyEligibilityRequest = undefined;

      const parametersStore = useParametersStore();
      parametersStore.clearEntityParameterSettings();
    },
  },
});
